import { Component, ViewChild, ElementRef, Input, Output, EventEmitter, OnInit, OnDestroy, HostListener, ChangeDetectorRef, SimpleChanges, Renderer2 } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { AxinomDrmService } from 'src/app/_service/axinom-drm.service';
import { DrmCapabilityService } from 'src/app/_service/drm-capability.service';
import { IndexedDbServiceService } from 'src/app/_service/indexed-db-service.service';
import { SentryPerformanceService } from 'src/app/_service/sentry-performance.service';
import { DeviceService } from 'src/app/_service/tv-detect.service';
import { URLS } from 'src/app/constants/URLS';
import * as Sentry from '@sentry/angular';

declare const shaka: any;
declare const google: any;

@Component({
  selector: 'app-vids-player',
  templateUrl: './vids-player.component.html',
  styleUrls: ['./vids-player.component.css']
})

export class VidsPlayerComponent implements OnInit, OnDestroy {
  @ViewChild('videoPlayer', { static: true }) videoElementRef!: ElementRef<HTMLVideoElement>;
  @ViewChild('videoContainer', { static: true }) videoContainerRef!: ElementRef<HTMLDivElement>;
  @ViewChild('progressBar', { static: true }) progressBarRef!: ElementRef<HTMLDivElement>;
  @Input() src: string = '';
  @Input() poster: string = '';
  @Input() title: string = '';
  @Input() contentType: string = '';
  @Input() startTime: number = 0;
  @Input() segments: any[] = [];
  @Input() video: any;
  @Input() isLive: boolean = false;
  @Input() liveViewerCount: number = 0;
  currentSegment: any = null;
  @Output() ended = new EventEmitter<void>();
  @Output() timeUpdate = new EventEmitter<number>();
  @Output() onBackClick = new EventEmitter<void>();
  @Output() playbackLimitReached = new EventEmitter<void>();

  // Session watch time tracking — tracks only real streaming seconds
  sessionWatchTime: number = 0;
  private _watchTimerStart: number | null = null;

  // Playback duration limit tracking (localStorage-based)
  playbackLimitExceeded: boolean = false;
  remainingPlaybackTime: number = 0; // in seconds
  private playbackLimitMs: number = 0; // total allowed in ms
  private consumedWatchTime: number = 0; // previously consumed in ms
  private playbackLimitKey: string = '';
  private playbackLimitCheckInterval: any = null;

  isPlaying: boolean = false;
  isMuted: boolean = localStorage.getItem('videoMuted') === 'true';
  isFullscreen: boolean = false;
  isBuffering: boolean = false;
  isLoading: boolean = true;
  canPlay: boolean = false;
  currentTime: number = 0;
  duration: number = 0;
  bufferedPercent: number = 0;
  progressPercent: number = 0;
  // --- Live DVR (YouTube-style timeshift over Shaka's seekable window) ---
  liveProgressPercent: number = 100;   // playhead position within seekRange()
  liveBufferedPercent: number = 100;
  behindLiveSeconds: number = 0;       // seekRange().end - currentTime
  isAtLiveEdge: boolean = true;
  liveWindowSeconds: number = 0;       // DVR depth = seekRange().end - start (~2h)
  private liveDvrInterval: any = null;
  private readonly LIVE_EDGE_THRESHOLD = 12; // within N s of the edge counts as "live"
  volume: number = parseFloat(localStorage.getItem('videoVolume') || '1');
  previousVolume: number = parseFloat(localStorage.getItem('videoVolume') || '1');
  showVolumeSlider: boolean = false;
  controlsTimer: any;
  isDragging: boolean = false;
  isHovering: boolean = false;
  private isDestroyed: boolean = false;
  private touchStartTime: number = 0;
  private touchStartX: number = 0;
  private touchStartY: number = 0;
  private lastTapTime: number = 0;
  private doubleTapThreshold: number = 300;
  private swipeThreshold: number = 50;
  private longPressTimer: any;
  private longPressThreshold: number = 500;
  private orientationAutoEnabled = false;
  skipIndicatorText: string = '';
  skipIndicatorSide: 'left' | 'right' | '' = '';
  showSkipIndicator: boolean = false; 
  availableQualities: any[] = [];
  currentQuality: any = null;
  showQualityMenu: boolean = false;
  availableAudioTracks: any[] = [];
  currentAudioTrack: any = null;
  showAudioMenu: boolean = false;
  availableTextTracks: any[] = [];
  currentTextTrack: any = null;
  showSubtitleMenu: boolean = false;
  playbackRates = [0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2];
  currentPlaybackRate: number = 1;
  showPlaybackRateMenu: boolean = false;
  showSettingsMenu: boolean = false;
  showAudioSubtitlePanel: boolean = false;
  activeSettingsTab: string = 'quality';
  showMobileSettings: boolean = false;
  showProgressPreview: boolean = false;
  progressPreviewTime: number = 0;
  progressPreviewPercent: number = 0;
  videoElement!: HTMLVideoElement;
  player: any;
  private boundProgressMouseMove = this.onProgressBarMouseMove.bind(this);
  private boundProgressMouseUp = this.onProgressBarMouseUp.bind(this);
  private boundFullscreenChange = this.onFullscreenChange.bind(this);
  @ViewChild('videoContainer') videoContainer!: ElementRef;
  isControlsVisible = true;
  private controlsTimeout: any;
  private readonly CONTROLS_TIMEOUT_DURATION = 3000;
  private isTogglingControls = false;
  private wasControlsVisibleOnTouchStart: boolean = false;
  private hideTimeout: any;
  private doubleTapSeekAmount: number = 0; 
  private doubleTapSequenceTimer: any = null; 
  private readonly DOUBLE_TAP_SEQUENCE_WINDOW: number = 800;
  @Input() doubleTapSeekIncrement: number = 10;
  private mouseIdleTimer: any;
  private readonly MOUSE_IDLE_TIMEOUT = 2500;
  errorMessage: string = '';
  hasPlaybackError: boolean = false;
  isGeoRestricted: boolean = false;
  drmConfig: any;

  private adManager: any;
  private adContainer!: HTMLElement;
  isAdPlaying = false;

  // Ad transparency labeling (COMP-05)
  adBadgeType: 'ad' | 'promo' = 'ad';
  adRemainingTime: number = 0;
  adClickUrl: string | null = null;
  private adCountdownInterval: any = null;
  // Debounces ad-break teardown so it doesn't fire between ads in a pod
  // ("Ad 1 of 2" -> "Ad 2 of 2"); the next ad's load/start cancels it.
  private adEndTimer: any = null;
  private shakaUi: any;
  private shakaControls: any;
  private pendingSeekTime: number | null = null;

  private isAes128Hls = false;
  private lastMediaUrl = '';
  private readonly HLS_KEY_HOST = 'clearkey.ezdrm.com';
  private readonly HLS_KEY_PATH = '/getKey.aspx';

  private readonly RETRY_DELAYS = [800, 1500, 2500]; 

  private autoRetryCount = 0;
  private readonly MAX_AUTO_RETRY = 3;
  private retryTimer: any = null;
  private spinnerKillTimer: any = null;
  
  private drmLicenceCount: number = 0;

  constructor(
    private cdr: ChangeDetectorRef,
    private renderer: Renderer2,
    private http: HttpClient,
    private axinomService: AxinomDrmService,
    private indexedDBService: IndexedDbServiceService,
    private drmCapability: DrmCapabilityService,
    private sentryPerf: SentryPerformanceService,
    private deviceService: DeviceService
    ) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes['startTime'] && !changes['startTime'].firstChange && this.startTime > 0 && this.contentType != 'trailer') {
      this.seek(this.startTime);
      this.pendingSeekTime = this.startTime;
    }
    if (changes['video'] && this.video) {
      this.initPlaybackLimit();
    }
  }

  async ngOnInit() {
    this.videoElement = this.videoElementRef.nativeElement;
    setInterval(() => {
      if (this.isPlaying) {
        localStorage.setItem('videoCurrentTime', this.videoElement.currentTime.toString());
      }
    }, 1000);
    this.videoElement.volume = this.volume;
    this.videoElement.muted = this.isMuted;
    this.setupEventListeners();
    this.resetControlsTimer();
    const savedTime = localStorage.getItem('videoCurrentTime');
    if (savedTime) {
      this.videoElement.currentTime = parseFloat(savedTime);
    }
    document.addEventListener('fullscreenchange', this.boundFullscreenChange);
    document.addEventListener('webkitfullscreenchange', this.boundFullscreenChange);
    document.addEventListener('mozfullscreenchange', this.boundFullscreenChange);
    document.addEventListener('MSFullscreenChange', this.boundFullscreenChange);
    if (this.isMobile()) {
      this.resetControlsTimer();
    }
    document.addEventListener('visibilitychange', this.boundVisibilityChange);
    window.addEventListener('beforeunload', this.boundBeforeUnload);
  }

  private boundProgressTouchMove = this.onProgressBarTouchMove.bind(this);
  private boundProgressTouchEnd = this.onProgressBarTouchEnd.bind(this);
  private boundVisibilityChange = this.onVisibilityChange.bind(this);
  private boundBeforeUnload = this.onBeforeUnload.bind(this);

  ngAfterViewInit(): void {
    const container = this.videoContainer.nativeElement;
    container.addEventListener('mousemove', this.handleMouseMove);
    const progressBar = this.progressBarRef.nativeElement;
    progressBar.addEventListener('touchstart', this.onProgressBarTouchStart.bind(this), { passive: false });
    this.setControlsAutoHide();
  }

  ngOnDestroy() {
    this.stopWatchTimer();
    this.stopAdCountdown();
    if (this.adEndTimer) {
      clearTimeout(this.adEndTimer);
      this.adEndTimer = null;
    }
    this.stopLiveDvrTracking();
    this.persistWatchTime();
    if (this.playbackLimitCheckInterval) {
      clearInterval(this.playbackLimitCheckInterval);
    }
    document.removeEventListener('visibilitychange', this.boundVisibilityChange);
    window.removeEventListener('beforeunload', this.boundBeforeUnload);
    localStorage.removeItem('videoCurrentTime');
    this.isDestroyed = true;
    if (this.controlsTimer) {
      clearTimeout(this.controlsTimer);
      this.controlsTimer = null;
    }
    if (this.longPressTimer) {
      clearTimeout(this.longPressTimer);
      this.longPressTimer = null;
    }
    if (this.doubleTapSequenceTimer) {
      clearTimeout(this.doubleTapSequenceTimer);
    }
    if (this.skipIndicatorTimer) {
      clearTimeout(this.skipIndicatorTimer);
    }
    if (this.retryTimer) {
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }
    if (this.spinnerKillTimer) clearInterval(this.spinnerKillTimer);
    document.removeEventListener('mousemove', this.boundProgressMouseMove);
    document.removeEventListener('mouseup', this.boundProgressMouseUp);
    document.removeEventListener('fullscreenchange', this.boundFullscreenChange);
    document.removeEventListener('webkitfullscreenchange', this.boundFullscreenChange);
    document.removeEventListener('mozfullscreenchange', this.boundFullscreenChange);
    document.removeEventListener('MSFullscreenChange', this.boundFullscreenChange);
    window.removeEventListener("orientationchange", this.handleAutoRotation);
    if (this.isMobile() && screen.orientation && (screen.orientation as any).unlock) {
      try {
        (screen.orientation as any).unlock();
      } catch (error) {
        console.warn('Could not unlock orientation:', error);
      }
    }
    if (this.player) {
      try {
        this.player.destroy();
        this.player = null;
      } catch (error) {
        console.warn('Error destroying player:', error);
      }
    }
    const container = this.videoContainer?.nativeElement;
    if (container) {
      container.removeEventListener('mousemove', this.handleMouseMove);
    }
    const progressBar = this.progressBarRef?.nativeElement;
    if (progressBar) {
      progressBar.removeEventListener('touchstart', this.onProgressBarTouchStart.bind(this));
      document.removeEventListener('touchmove', this.boundProgressTouchMove);
      document.removeEventListener('touchend', this.boundProgressTouchEnd);
    }
    clearTimeout(this.controlsTimeout);
    
  }

  onInteractionClick(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    this.toggleControlsVisibility();
  }
  
  onProgressBarTouchStart(event: TouchEvent) {
    event.preventDefault();
    this.isDragging = true;
    this.showVideoControls(); 
    this.onProgressBarClick(event); 
    document.addEventListener('touchmove', this.boundProgressTouchMove, { passive: false });
    document.addEventListener('touchend', this.boundProgressTouchEnd, { passive: false });
  }

  private onProgressBarTouchMove(event: TouchEvent) {
    if (this.isDragging) {
      event.preventDefault(); 
      this.onProgressBarClick(event); 
      this.showVideoControls();
    }
  }

  private onProgressBarTouchEnd(event: TouchEvent) {
    if (this.isDragging) {
      this.isDragging = false;
      this.onProgressBarClick(event); 
      document.removeEventListener('touchmove', this.boundProgressTouchMove);
      document.removeEventListener('touchend', this.boundProgressTouchEnd);
      this.resetControlsTimer(); 
    }
  }

  private handleMouseMove = () => {
    if (this.isAdPlaying) return;
    this.showControlss();
    this.resetMouseIdleTimer();
  };
  
  private resetMouseIdleTimer(): void {
    clearTimeout(this.mouseIdleTimer);
    this.renderer.removeClass(this.videoContainerRef.nativeElement, 'cursor-hidden'); 
    this.mouseIdleTimer = setTimeout(() => {
      if (this.isPlaying && !this.isControlsVisible && !this.isAnyMenuOpen()) {
        this.renderer.addClass(this.videoContainerRef.nativeElement, 'cursor-hidden');
      }
    }, this.MOUSE_IDLE_TIMEOUT);
  }

  private handleUserInteraction = () => {
    if (!this.isTogglingControls) {
      this.showControlss();
    }
  };

  showVideoControls() {
    if (this.isAdPlaying) return;
    if (!this.isTogglingControls) {
      this.isControlsVisible = true
      this.isHovering = true;
      this.resetControlsTimer();
      this.cdr.detectChanges();
    }
  }

  private showControlss(): void {
    if (this.isAdPlaying) return;
    this.isControlsVisible = true;
    clearTimeout(this.hideTimeout);
    this.renderer.removeClass(this.videoContainerRef.nativeElement, 'cursor-hidden'); 
    this.hideTimeout = setTimeout(() => {
      this.hideControls();
    }, 3000);
  }
  
  private hideControls(): void {
    if (this.isAdPlaying) return;
    this.isControlsVisible = false;
    clearTimeout(this.hideTimeout);
    this.renderer.addClass(this.videoContainerRef.nativeElement, 'cursor-hidden');
  }
  
  private setControlsAutoHide(): void {
    if (this.isAdPlaying) return;
    clearTimeout(this.controlsTimeout);
    this.controlsTimeout = setTimeout(() => {
      this.hideControls();
    }, this.CONTROLS_TIMEOUT_DURATION);
  }

  seek(time: number) {
    if (this.isDestroyed || !this.videoElement) return;
    try {
      this.videoElement.currentTime = time;
      this.currentTime = time;
    } catch (error) {
      console.error('❌ Seek failed:', error);
    }
  }

  async initShakaPlayer(mediaUrl: string, drmConfig: any) {
    console.log(drmConfig)
    this.lastMediaUrl = mediaUrl;
    this.drmConfig = drmConfig;
    const videoId = this.video?.vidLibId || '';
    this.sentryPerf.startVideoLoad(videoId, { title: this.title, channelId: this.video?.channelId });
    this.isAes128Hls = await this.detectAes128Hls(mediaUrl);
    console.log('isAes128Hls:', this.isAes128Hls);
    shaka.polyfill.installAll();
    if (!shaka.Player.isBrowserSupported()) throw new Error("Browser not supported");
    if (this.player) {
      await this.player.destroy().catch(()=>{});
    }
    this.player = new shaka.Player(this.videoElement);
    const net = this.player.getNetworkingEngine();
    net.registerRequestFilter((type, request) => {
      const uri = request?.uris?.[0] || '';
      if (uri.includes(this.HLS_KEY_HOST) && uri.includes(this.HLS_KEY_PATH)) {
        console.warn('🔑 HLS AES-128 KEY REQUEST:', uri);
        request.allowCrossSiteCredentials = false;
        request.retryParameters = {
          maxAttempts: 6,
          baseDelay: 800,
          backoffFactor: 2,
          fuzzFactor: 0.5,
          timeout: 0
        };
      }
      if (uri.includes('.ts') || uri.includes('.m4s')) {
        console.log('📦 SEGMENT REQ:', uri);
      }
      if (uri.includes('.ts') || uri.includes('.m4s')) {
        request.retryParameters = {
          maxAttempts: 6,
          baseDelay: 1000,
          backoffFactor: 2,
          fuzzFactor: 0.5,
          timeout: 0
        };
      }
    });
    this.player.configure({
      streaming: {
        retryParameters: {
          maxAttempts: 6,
          baseDelay: 1000,
          backoffFactor: 2,
          fuzzFactor: 0.5,
          timeout: 0
        }
      },
      drm: {
        retryParameters: {
          maxAttempts: 4,
          baseDelay: 1000,
          backoffFactor: 2,
          fuzzFactor: 0.5,
          timeout: 0
        }
      },
      manifest: {
        retryParameters: {
          maxAttempts: 6,
          baseDelay: 1000,
          backoffFactor: 2,
          fuzzFactor: 0.5,
          timeout: 0
        },
        hls: {
          ignoreTextStreamFailures: true
        }
      },
      abr: {
        enabled: false, 
        defaultBandwidthEstimate: 700_000,
        switchInterval: 8
      }
    });
    if (this.spinnerKillTimer) clearInterval(this.spinnerKillTimer);
    this.spinnerKillTimer = setInterval(() => {
      document.querySelectorAll('.shaka-spinner-container, .shaka-spinner').forEach(el => (el as HTMLElement).style.display = 'none');
    }, 500);   
    this.player.addEventListener('error', (event: any) => {
      const shakaError = event?.detail;
      console.log('shakaerror', shakaError);
      this.handleShakaError(shakaError);
    });
    const persistentSupported = await this.drmCapability.supportsPersistentLicense();
    // this.player.getNetworkingEngine().registerRequestFilter((type, request) => {
    //   const token = this.waitForToken();
    //   if (request?.uris[0]?.includes('post_videos')) {
    //     request.headers["Authorization"] = token;
    //   }
    // });
    if (!this.isHls() && this.video?.external_source !== 'true') {
      const token = await this.waitForToken();
      this.player.getNetworkingEngine().registerRequestFilter((type, req) => {
        if (type === shaka.net.NetworkingEngine.RequestType.LICENSE) {
          req.headers["X-AxDRM-Message"] = token;
          req.headers["Content-Type"] = "application/octet-stream";
          this.sentryPerf.startDrmLicense(videoId);
        }
      });
      let licenceTracked = false;
      this.player.getNetworkingEngine().registerResponseFilter((type, response) => {
        if (type === shaka.net.NetworkingEngine.RequestType.LICENSE) {
          this.sentryPerf.endDrmLicense(videoId);
          if (!licenceTracked) {
            licenceTracked = true;
            this.drmLicenceCount++;
            this.trackDrmLicence(videoId, 'success');
          }
        }
      });
    }
    const savedRaw = localStorage.getItem("drm_persistent_session");
    let restore = {};
    if (!this.isHls() && savedRaw) {
      const saved = JSON.parse(savedRaw);
      restore = {
        persistentSessionOnlinePlayback: true,
        persistentSessionsMetadata: [{
          sessionId: saved.sessionId,
          initData: new Uint8Array(saved.initData), 
          initDataType: saved.initDataType ?? "cenc"
        }]
      };
    }
    if (!this.isHls()) {
      this.player.configure({
        drm: {
          ...restore,
          servers: drmConfig?.servers,
          advanced: {
            "com.widevine.alpha": {
              sessionType: persistentSupported ? 'persistent-license' : 'temporary',
              persistentStateRequired: persistentSupported,
            }
          },
        },
      });
    } 
    this.player.addEventListener("drmsessionupdate", () => {
      const sessions = this.player.getActiveSessionsMetadata();
      let p = sessions.find(s => s.sessionType === "persistent-license");
      if (p) {
        localStorage.setItem(
          "drm_persistent_session",
          JSON.stringify({
            sessionId: p.sessionId,
            initData: Array.from(p.initData),
            initDataType: p.initDataType
          })
        );
      }
    });
    console.log('adsssss', this.shouldPlayAds())
    if (this.shouldPlayAds()) {
      await this.initAds();
    }
    await this.player.load(mediaUrl);
    if (this.isLive) this.startLiveDvrTracking();
    this.sentryPerf.endVideoLoad(videoId);
    this.preferStartQuality(480);
    setTimeout(() => {
      this.player.configure({ abr: { enabled: true } });
      console.log("✅ ABR Auto enabled");
    }, 10000);
    this.autoRetryCount = 0;
    this.drmLicenceCount = 0;
    this.hasPlaybackError = false;
    this.isGeoRestricted = false;
    this.errorMessage = "";
    this.cdr.detectChanges();
    setTimeout(() => {
      const activeSessions = this.player?.getMediaElement()?.webkitMediaKeyStatusMap || new Map();
    }, 2000);
    this.loadAvailableTracks();
    this.cdr.detectChanges();
    let lastTrackedHeight = 0;
    this.player.addEventListener('adaptation', () => {
      if (this.currentQuality?.isAuto) {
        const autoLabel = this.getAutoQualityLabel();
        this.currentQuality.label = autoLabel;
        this.cdr.detectChanges();
      }
      try {
        const activeTracks = this.player.getVariantTracks().filter((t: any) => t.active);
        const activeTrack = activeTracks[0];
        if (activeTrack && activeTrack.height !== lastTrackedHeight) {
          this.sentryPerf.trackQualityChange(videoId, lastTrackedHeight, activeTrack.height, activeTrack.bandwidth || 0);
          lastTrackedHeight = activeTrack.height;
        }
      } catch (e) {}
    });
    if (!this.shouldPlayAds() && !this.playbackLimitExceeded) {
      this.videoElement.play().catch(() => {});
    }
    // this.videoElement.play().catch(()=>{});
  }

  loadAvailableTracks() {
    try {
      const variants = this.player.getVariantTracks();
      const qualityMap = new Map();
      variants.forEach((variant: any) => {
        if (variant.height && variant.height > 0) {
          const key = variant.height.toString();
          if (!qualityMap.has(key)) {
            qualityMap.set(key, {
              label: `${variant.height}p`,
              height: variant.height,
              id: key
            });
          }
        }
      });
      const autoLabel = this.getAutoQualityLabel();
      this.availableQualities = [
        { label: autoLabel, id: 'auto', isAuto: true },
        ...Array.from(qualityMap.values())
        .sort((a: any, b: any) => b.height - a.height)
      ];
      const savedQuality = localStorage.getItem('videoQuality');
      const matchedQuality = savedQuality
        ? this.availableQualities.find((q: any) =>
            savedQuality === 'auto' ? q.isAuto : String(q.height) === savedQuality)
        : null;
      if (matchedQuality) {
        this.selectQuality(matchedQuality);
      } else {
        const nonAutoQualities = this.availableQualities.filter((q: any) => !q.isAuto);
        const closest = nonAutoQualities.reduce((prev: any, curr: any) =>
          Math.abs(curr.height - 480) < Math.abs(prev.height - 480) ? curr : prev
        );
        this.selectQuality(closest);
      }

      const audioMap = new Map();
      variants.forEach((variant: any) => {
        if (variant.language) {
          audioMap.set(variant.language, {
            label: this.getLanguageName(variant.language),
            language: variant.language,
            id: variant.language
          });
        }
      });
      this.availableAudioTracks = Array.from(audioMap.values());
      const savedAudio = localStorage.getItem('videoAudioTrack');
      const matchedAudio = savedAudio
        ? this.availableAudioTracks.find((a: any) => a.language === savedAudio)
        : null;
      if (matchedAudio) {
        this.selectAudioTrack(matchedAudio);
      } else {
        this.currentAudioTrack = this.availableAudioTracks[0] || null;
      }

      const textTracks = this.player.getTextTracks();
      this.availableTextTracks = [
        { label: 'Off', language: 'off', id: 'off' },
        ...textTracks.map((track: any) => ({
          label: this.getLanguageName(track.language),
          language: track.language,
          id: track.language
        }))
      ];
      const savedText = localStorage.getItem('videoTextTrack');
      const matchedText = savedText
        ? this.availableTextTracks.find((t: any) => t.language === savedText)
        : null;
      if (matchedText) {
        this.selectTextTrack(matchedText);
      } else {
        this.currentTextTrack = this.availableTextTracks[0];
      }
    } catch (error) {
      console.error('Error loading tracks:', error);
    }
  }

  getLanguageName(code: string): string {
    const names: { [key: string]: string } = {
      'en': 'English', 'es': 'Spanish', 'fr': 'French', 'de': 'German',
      'it': 'Italian', 'pt': 'Portuguese', 'ru': 'Russian', 'ja': 'Japanese',
      'ko': 'Korean', 'zh': 'Chinese', 'ar': 'Arabic', 'hi': 'Hindi'
    };
    return names[code] || code.toUpperCase();
  }

  setupEventListeners() {
    const video = this.videoElement;
    video.addEventListener('loadstart', () => {
      this.isLoading = true;
    });
    video.addEventListener('loadeddata', () => {
      this.isLoading = false;
      this.canPlay = true;
    });
    video.addEventListener('loadedmetadata', () => {
      this.duration = video.duration || 0;
    });
    video.addEventListener('timeupdate', () => {
      if (!this.isDragging) {
        this.currentTime = video.currentTime;
        this.progressPercent = this.duration > 0 ? (this.currentTime / this.duration) * 100 : 0;
        this.timeUpdate.emit(this.currentTime);
        this.checkSegment();
        if (this.isLive) this.updateLiveDvr();
      }
    });
    video.addEventListener('progress', () => {
      if (video.buffered.length > 0 && this.duration > 0) {
        const bufferedEnd = video.buffered.end(video.buffered.length - 1);
        this.bufferedPercent = (bufferedEnd / this.duration) * 100;
      }
    });
    video.addEventListener('play', () => {
      if (this.playbackLimitExceeded) {
        video.pause();
        return;
      }
      this.isPlaying = true;
      this.isBuffering = false;
    });
    video.addEventListener('pause', () => {
      this.isPlaying = false;
      this.stopWatchTimer();
      this.persistWatchTime();
    });
    video.addEventListener('ended', () => {
      localStorage.removeItem('videoCurrentTime');
      this.isPlaying = false;
      this.stopWatchTimer();
      this.persistWatchTime();
      this.ended.emit();
    });
    video.addEventListener('waiting', () => {
      this.isBuffering = true;
      this.sentryPerf.trackBuffering(this.video?.vidLibId || '');
      this.stopWatchTimer();
    });
    video.addEventListener('seeking', () => {
      this.stopWatchTimer();
    });
    video.addEventListener('seeked', () => {
      if (!video.paused) {
        this.startWatchTimer();
      }
    });
    video.addEventListener('canplay', () => {
      this.isBuffering = false;
      this.sentryPerf.trackBufferingEnd(this.video?.vidLibId || '');
      this.canPlay = true;
      if (this.hasPlaybackError) {
        this.hasPlaybackError = false;
        this.errorMessage = '';
        this.cdr.detectChanges();
      }
    });
    video.addEventListener('volumechange', () => {
      this.volume = video.volume;
      this.isMuted = video.muted;
    });
    video.addEventListener('playing', () => {
      if (!this.isAdPlaying) {
        this.startWatchTimer();
      }
      if (this.hasPlaybackError) {
        this.hasPlaybackError = false;
        this.errorMessage = '';
        this.cdr.detectChanges();
      }
    });
  }

  private startWatchTimer() {
    if (!this._watchTimerStart) {
      this._watchTimerStart = Date.now();
    }
  }

  private stopWatchTimer() {
    if (this._watchTimerStart) {
      this.sessionWatchTime += Date.now() - this._watchTimerStart;
      this._watchTimerStart = null;
    }
  }

  flushSessionWatchTime(): number {
    this.stopWatchTimer();
    return this.sessionWatchTime;
  }

  private onVisibilityChange() {
    if (document.hidden) {
      this.stopWatchTimer();
      this.persistWatchTime();
    } else if (this.isPlaying && !this.isAdPlaying && !this.playbackLimitExceeded) {
      this.startWatchTimer();
    }
  }

  private onBeforeUnload() {
    this.stopWatchTimer();
    this.persistWatchTime();
  }

  // --- Playback duration limit ---

  private initPlaybackLimit() {
    if (this.video?.preparedVideoType !== 'PPV' || this.contentType === 'trailer') {
      // Reset PPV state when switching to a non-PPV video
      this.playbackLimitExceeded = false;
      this.playbackLimitMs = 0;
      this.playbackLimitKey = '';
      if (this.playbackLimitCheckInterval) {
        clearInterval(this.playbackLimitCheckInterval);
        this.playbackLimitCheckInterval = null;
      }
      return;
    }
    const playbackDuration = parseFloat(this.video?.playback_duration || '0');
    if (!playbackDuration || !this.video?.vidLibId) return;

    // Clear any existing interval from a previous video
    if (this.playbackLimitCheckInterval) {
      clearInterval(this.playbackLimitCheckInterval);
      this.playbackLimitCheckInterval = null;
    }

    const userId = localStorage.getItem('userId') || 'anonymous';
    this.playbackLimitKey = `ppv_watch_${userId}_${this.video.vidLibId}`;
    this.playbackLimitMs = playbackDuration * 3600 * 1000; // hours to ms
    // Reset session time so we don't count pre-init accumulation
    this.stopWatchTimer();
    this.sessionWatchTime = 0;
    this.consumedWatchTime = parseInt(localStorage.getItem(this.playbackLimitKey) || '0', 10);

    this.updateRemainingTime();

    if (this.consumedWatchTime >= this.playbackLimitMs) {
      this.playbackLimitExceeded = true;
      this.videoElement?.pause();
      this.cdr.detectChanges();
      return;
    }

    this.playbackLimitExceeded = false;
    if (this.isPlaying) this.startWatchTimer();

    // Check limit every 5 seconds during playback
    this.playbackLimitCheckInterval = setInterval(() => {
      if (!this.isPlaying || !this.playbackLimitMs || this.isAdPlaying) return;
      this.persistWatchTime();
      this.updateRemainingTime();

      if (this.getTotalConsumedMs() >= this.playbackLimitMs) {
        this.onPlaybackLimitReached();
      }
    }, 5000);
  }

  private getTotalConsumedMs(): number {
    let current = this.sessionWatchTime;
    if (this._watchTimerStart) {
      current += Date.now() - this._watchTimerStart;
    }
    return this.consumedWatchTime + current;
  }

  private updateRemainingTime() {
    const remainingMs = Math.max(0, this.playbackLimitMs - this.getTotalConsumedMs());
    this.remainingPlaybackTime = Math.ceil(remainingMs / 1000);
  }

  private persistWatchTime() {
    if (!this.playbackLimitKey) return;
    localStorage.setItem(this.playbackLimitKey, this.getTotalConsumedMs().toString());
  }

  private onPlaybackLimitReached() {
    this.persistWatchTime();
    this.playbackLimitExceeded = true;
    this.remainingPlaybackTime = 0;
    this.videoElement.pause();
    this.playbackLimitReached.emit();
    this.cdr.detectChanges();
  }

  formatRemainingTime(): string {
    const totalSeconds = this.remainingPlaybackTime;
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    }
    return `${seconds}s`;
  }

  togglePlay() {
    if (this.isAdPlaying || !this.canPlay) return;
    if (this.playbackLimitExceeded) return;
    this.showVideoControls();
    if (this.isPlaying) {
      this.videoElement.pause();
    } else {
      this.videoElement.play().catch(error => {
        console.error('Play failed:', error);
      });
    }
  }

  selectQuality(quality: any) {
    try {
      if (quality.isAuto) {
        this.player.configure({ abr: { enabled: true } });
      } else {
        this.player.configure({ abr: { enabled: false } });
        const variants = this.player.getVariantTracks();
        const targetVariant = variants.find((v: any) => v.height === quality.height);
        if (targetVariant) {
          this.player.selectVariantTrack(targetVariant, true);
        }
      }
      this.currentQuality = quality;
      localStorage.setItem('videoQuality', quality.isAuto ? 'auto' : String(quality.height));
      this.closeAllMenus();
    } catch (error) {
      console.error('Quality selection error:', error);
    }
  }

  selectAudioTrack(audioTrack: any) {
    try {
      const variants = this.player.getVariantTracks();
      const targetVariant = variants.find((v: any) => v.language === audioTrack.language);
      if (targetVariant) {
        this.player.selectVariantTrack(targetVariant, true);
      }
      this.currentAudioTrack = audioTrack;
      localStorage.setItem('videoAudioTrack', audioTrack.language);
      this.closeAllMenus();
    } catch (error) {
      console.error('Audio track error:', error);
    }
  }

  selectTextTrack(textTrack: any) {
    try {
      if (textTrack.language === 'off') {
        this.player.setTextTrackVisibility(false);
      } else {
        const textTracks = this.player.getTextTracks();
        const targetTrack = textTracks.find((t: any) => t.language === textTrack.language);
        if (targetTrack) {
          this.player.selectTextTrack(targetTrack);
          this.player.setTextTrackVisibility(true);
        }
      }
      this.currentTextTrack = textTrack;
      localStorage.setItem('videoTextTrack', textTrack.language);
      this.closeAllMenus();
    } catch (error) {
      console.error('Subtitle error:', error);
    }
  }

  toggleMute() {
    if (this.isMuted) {
      this.videoElement.muted = false;
      this.videoElement.volume = this.previousVolume;
    } else {
      this.previousVolume = this.volume;
      this.videoElement.muted = true;
    }
    localStorage.setItem('videoMuted', String(this.videoElement.muted));
  }

  setVolume(event: Event) {
    const target = event.target as HTMLInputElement;
    const volume = parseFloat(target.value);
    this.videoElement.volume = volume;
    this.videoElement.muted = volume === 0;
    localStorage.setItem('videoVolume', String(volume));
    localStorage.setItem('videoMuted', String(volume === 0));
  }

  onProgressBarClick(event: MouseEvent | TouchEvent) {
    const rect = this.progressBarRef.nativeElement.getBoundingClientRect();
    let clientX: number;
    if (event instanceof MouseEvent) {
      clientX = event.clientX;
    } else {
      clientX = event.touches[0]?.clientX || event.changedTouches[0]?.clientX;
    }
    const percent = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    if (this.isLive) {
      // Seek within the DVR window instead of the (Infinite) VOD duration.
      this.liveProgressPercent = percent * 100;
      this.seekLiveToPercent(percent);
      return;
    }
    if (this.duration <= 0) return;
    const newTime = percent * this.duration;
    this.videoElement.currentTime = newTime;
    this.currentTime = newTime;
    this.progressPercent = percent * 100;
    this.cdr.detectChanges();
  }

  onProgressBarMouseDown(event: MouseEvent) {
    event.preventDefault();
    this.isDragging = true;
    this.onProgressBarClick(event);
    document.addEventListener('mousemove', this.boundProgressMouseMove);
    document.addEventListener('mouseup', this.boundProgressMouseUp);
  }

  private onProgressBarMouseMove(event: MouseEvent) {
    if (this.isDragging) {
      this.onProgressBarClick(event);
    }
  }

  private onProgressBarMouseUp() {
    this.isDragging = false;
    document.removeEventListener('mousemove', this.boundProgressMouseMove);
    document.removeEventListener('mouseup', this.boundProgressMouseUp);
  }

  onProgressBarHover(event: MouseEvent) {
    if (!this.isMobile()) {
      const rect = this.progressBarRef.nativeElement.getBoundingClientRect();
      const percent = Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width));
      this.progressPreviewPercent = percent * 100;
      this.progressPreviewTime = percent * this.duration;
      this.showProgressPreview = true;
    }
  }

  onProgressBarLeave() {
    this.showProgressPreview = false;
  }

  // ============================================================
  // LIVE DVR (YouTube-style timeshift)
  // ============================================================

  private startLiveDvrTracking(): void {
    this.stopLiveDvrTracking();
    this.updateLiveDvr();
    // The live edge advances in real time, so refresh once a second even when
    // 'timeupdate' isn't firing (e.g. paused, or sitting at the edge).
    this.liveDvrInterval = setInterval(() => this.updateLiveDvr(), 1000);
  }

  private stopLiveDvrTracking(): void {
    if (this.liveDvrInterval) {
      clearInterval(this.liveDvrInterval);
      this.liveDvrInterval = null;
    }
  }

  /** Recomputes the DVR window + playhead position from Shaka's seekRange(). */
  private updateLiveDvr(): void {
    if (!this.isLive || !this.player || this.isDragging) return;
    let range: { start: number; end: number };
    try { range = this.player.seekRange(); } catch { return; }
    if (!range || range.end <= range.start) return;

    const ct = this.videoElement?.currentTime ?? range.end;
    this.liveWindowSeconds = range.end - range.start;
    const pos = Math.max(0, Math.min(this.liveWindowSeconds, ct - range.start));
    this.liveProgressPercent = this.liveWindowSeconds > 0 ? (pos / this.liveWindowSeconds) * 100 : 100;
    this.behindLiveSeconds = Math.max(0, range.end - ct);
    this.isAtLiveEdge = this.behindLiveSeconds <= this.LIVE_EDGE_THRESHOLD;

    const v = this.videoElement;
    if (v && v.buffered.length > 0) {
      const bufferedEnd = v.buffered.end(v.buffered.length - 1);
      const bpos = Math.max(0, Math.min(this.liveWindowSeconds, bufferedEnd - range.start));
      this.liveBufferedPercent = this.liveWindowSeconds > 0 ? (bpos / this.liveWindowSeconds) * 100 : 100;
    }
    this.cdr.detectChanges();
  }

  /** Seek within the live DVR window to a 0..1 fraction of the seekable range. */
  seekLiveToPercent(percent: number): void {
    if (!this.player) return;
    let range: { start: number; end: number };
    try { range = this.player.seekRange(); } catch { return; }
    if (!range || range.end <= range.start) return;
    const clamped = Math.max(0, Math.min(1, percent));
    this.videoElement.currentTime = range.start + clamped * (range.end - range.start);
    this.updateLiveDvr();
  }

  /** Jump back to the live edge. */
  goToLive(): void {
    if (!this.player) return;
    let range: { start: number; end: number };
    try { range = this.player.seekRange(); } catch { return; }
    if (!range) return;
    // Sit a hair behind the edge so we stay inside the live buffer.
    this.videoElement.currentTime = Math.max(range.start, range.end - 1);
    if (this.videoElement.paused) this.videoElement.play().catch(() => {});
    this.isAtLiveEdge = true;
    this.behindLiveSeconds = 0;
    this.liveProgressPercent = 100;
    this.cdr.detectChanges();
  }

  /** "-1:23" / "-1:02:03" behind-live label. */
  formatBehindLive(seconds: number): string {
    const s = Math.max(0, Math.floor(seconds));
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    const mm = h > 0 ? String(m).padStart(2, '0') : String(m);
    const ss = String(sec).padStart(2, '0');
    return h > 0 ? `-${h}:${mm}:${ss}` : `-${mm}:${ss}`;
  }

  async toggleFullscreen() {
    const container = this.videoContainerRef.nativeElement;
    if (!this.isFullscreen) {
      try {
        if (container.requestFullscreen) {
          await container.requestFullscreen();
        } else if ((container as any).webkitRequestFullscreen) {
          await (container as any).webkitRequestFullscreen();
        } else if ((container as any).mozRequestFullScreen) {
          await (container as any).mozRequestFullScreen();
        } else if ((container as any).msRequestFullscreen) {
          await (container as any).msRequestFullscreen();
        }
        if (this.isMobile() && this.isOrientationAPISupported()) {
          try {
            await (screen.orientation as any).lock('landscape');
          } catch (error) {
            console.warn('Could not lock orientation to landscape:', error);
          }
        }
      } catch (error) {
        console.error('Error entering fullscreen:', error);
      }
    } else {
      try {
        if (document.exitFullscreen) {
          await document.exitFullscreen();
        } else if ((document as any).webkitExitFullscreen) {
          await (document as any).webkitExitFullscreen();
        } else if ((document as any).mozCancelFullScreen) {
          (document as any).mozCancelFullScreen();
        } else if ((document as any).msExitFullscreen) {
          (document as any).msExitFullscreen();
        }
        if (this.isMobile() && this.isOrientationAPISupported()) {
          try {
            (screen.orientation as any).unlock();
          } catch (error) {
            console.warn('Could not unlock orientation:', error);
          }
        }
      } catch (error) {
        console.error('Error exiting fullscreen:', error);
      }
    }
  }

  private onFullscreenChange() {
    this.isFullscreen = !!(document.fullscreenElement || (document as any).webkitFullscreenElement || (document as any).mozFullScreenElement || (document as any).msFullscreenElement);
    if (!this.isFullscreen && this.isMobile() && this.isOrientationAPISupported()) {
      try {
        (screen.orientation as any).unlock();
      } catch (error) {
        console.warn('Could not unlock orientation on fullscreen exit:', error);
      }
    }
    this.cdr.detectChanges();
  }

  isMobile(): boolean {
    return window.matchMedia("(pointer: coarse)").matches; 
  }  

  private isOrientationAPISupported(): boolean {
    return 'screen' in window && 'orientation' in screen && 'lock' in (screen.orientation as any);
  }

  @HostListener('touchstart', ['$event'])
  onTouchStart(event: TouchEvent) {
    if (event.touches.length === 1) {
      const target = event.target as HTMLElement;
      if (target.closest('.progress-track')) {
        return; 
      }
      this.wasControlsVisibleOnTouchStart = this.isControlsVisible;
      this.touchStartTime = Date.now();
      this.touchStartX = event.touches[0].clientX;
      this.touchStartY = event.touches[0].clientY;
      this.longPressTimer = setTimeout(() => {
        this.handleLongPress();
      }, this.longPressThreshold);
    }
    this.enableAutoOrientation();
  }

  @HostListener('touchmove', ['$event'])
  onTouchMove(event: TouchEvent) {
    if (this.longPressTimer) {
      clearTimeout(this.longPressTimer);
      this.longPressTimer = null;
    }
  }

  @HostListener('touchend', ['$event'])
  onTouchEnd(event: TouchEvent) {
    if (this.longPressTimer) {
      clearTimeout(this.longPressTimer);
      this.longPressTimer = null;
    }
    if (event.changedTouches.length === 1) {
      const touchEndTime = Date.now();
      const touchDuration = touchEndTime - this.touchStartTime;
      const touchEndX = event.changedTouches[0].clientX;
      const touchEndY = event.changedTouches[0].clientY;
      const deltaX = Math.abs(touchEndX - this.touchStartX);
      const deltaY = Math.abs(touchEndY - this.touchStartY);
      if (touchDuration < 300 && (deltaX > this.swipeThreshold || deltaY > this.swipeThreshold)) {
        this.handleSwipe(deltaX, deltaY, touchEndX - this.touchStartX, touchEndY - this.touchStartY);
        return;
      }
      if (touchDuration < this.doubleTapThreshold && deltaX < 30 && deltaY < 30) {
        if (this.lastTapTime && (touchEndTime - this.lastTapTime) < this.doubleTapThreshold) {
          this.handleDoubleTap(touchEndX);
          this.lastTapTime = 0; 
        } else {
          this.lastTapTime = touchEndTime;
          setTimeout(() => {
            if (this.lastTapTime === touchEndTime) {
              this.handleSingleTap();
            }
          }, this.doubleTapThreshold);
        }
      }
    }
  }

  private handleSingleTap() {
    if (this.isAdPlaying) return;
    this.toggleControlsVisibility();
  }

  private handleDoubleTap = this.debounce((touchX: number) => {
    if (this.isAdPlaying) return;
    if (!this.canPlay) return;
    this.showVideoControls();
    const screenWidth = window.innerWidth;
    const isLeftSide = touchX < screenWidth / 2;
    const seekIncrement = isLeftSide ? -10 : 10;
    const newTime = Math.max(0, Math.min(this.duration, this.videoElement.currentTime + seekIncrement));
    this.videoElement.currentTime = newTime;
    this.currentTime = newTime;
    this.progressPercent = this.duration > 0 ? (this.currentTime / this.duration) * 100 : 0;
    this.doubleTapSeekAmount += seekIncrement;
    this.displaySkipIndicator(
      `${this.doubleTapSeekAmount > 0 ? '+' : ''}${this.doubleTapSeekAmount}s`,
      isLeftSide ? 'left' : 'right'
    );
    if (this.doubleTapSequenceTimer) {
      clearTimeout(this.doubleTapSequenceTimer);
    }
    this.doubleTapSequenceTimer = setTimeout(() => {
      this.doubleTapSeekAmount = 0;
      this.doubleTapSequenceTimer = null;
    }, this.DOUBLE_TAP_SEQUENCE_WINDOW);
    this.lastTapTime = 0;
    this.cdr.detectChanges();
  }, 50); 

  private handleLongPress() {
    this.showVideoControls();
  }

  private handleSwipe(deltaX: number, deltaY: number, directionX: number, directionY: number) {
    if (this.isAdPlaying) return;
    if (this.isDragging) return; 
    this.showVideoControls();
    if (deltaX > deltaY) {
      const seekAmount = directionX > 0 ? 10 : -10;
      if (directionX > 0) {
        this.skipForward(Math.abs(seekAmount));
        this.displaySkipIndicator(`+${Math.abs(seekAmount)}s`, 'right');
      } else {
        this.skipBackward(Math.abs(seekAmount));
        this.displaySkipIndicator(`-${Math.abs(seekAmount)}s`, 'left');
      }
    }
  }

  private toggleControlsVisibility() {
    if (!this.wasControlsVisibleOnTouchStart) {
      this.showVideoControls();
    } else {
      this.hideVideoControls();
    }
  }

  setPlaybackRate(rate: number) {
    this.currentPlaybackRate = rate;
    this.videoElement.playbackRate = rate;
    this.closeAllMenus();
  }

  hideVideoControls() {
    this.isHovering = false;
    if (this.isMobile()) {
      if (!this.isAnyMenuOpen()) {
        this.isControlsVisible = false;
        this.cdr.detectChanges();
      }
    } else {
      setTimeout(() => {
        if (!this.isHovering && this.isPlaying && !this.isAnyMenuOpen()) {
          this.isControlsVisible = false;
          this.cdr.detectChanges();
        }
      }, 100);
    }
  }

  resetControlsTimer() {
    if (this.controlsTimer) {
      clearTimeout(this.controlsTimer);
    }
    const timeout = this.isMobile() ? 3000 : 3000; 
    this.controlsTimer = setTimeout(() => {
      if (this.isPlaying && !this.isAnyMenuOpen() && (this.isMobile() || !this.isHovering)) {
        this.hideVideoControls();
      }
    }, timeout);
  }

  private isAnyMenuOpen(): boolean {
    return this.showQualityMenu || this.showAudioMenu || this.showSubtitleMenu || this.showPlaybackRateMenu || this.showSettingsMenu || this.showVolumeSlider || this.showAudioSubtitlePanel || this.showMobileSettings;
  }

  closeAllMenus() {
    this.showQualityMenu = false;
    this.showAudioMenu = false;
    this.showSubtitleMenu = false;
    this.showPlaybackRateMenu = false;
    this.showSettingsMenu = false;
    this.showVolumeSlider = false;
    this.showAudioSubtitlePanel = false;
  }

  toggleSettingsMenu() {
    this.closeAllMenus();
    this.showSettingsMenu = !this.showSettingsMenu;
    if (this.showSettingsMenu) {
      this.showVideoControls();
    }
  }

  toggleMobileSettings() {
    const wasOpen = this.showMobileSettings;
    this.closeAllMenus();
    this.showMobileSettings = !wasOpen;
    if (this.showMobileSettings) {
      this.activeSettingsTab = 'quality';
      this.showVideoControls();
    }
  }

  closeMobileSettings() {
    this.showMobileSettings = false;
  }

  setSettingsTab(tab: string) {
    this.activeSettingsTab = tab;
  }

  toggleAudioSubtitlePanel() {
    const wasOpen = this.showAudioSubtitlePanel;
    this.closeAllMenus();
    this.showAudioSubtitlePanel = !wasOpen;
    if (this.showAudioSubtitlePanel) {
      this.showVideoControls();
    }
  }

  toggleQualityPanel() {
    const wasOpen = this.showQualityMenu;
    this.closeAllMenus();
    this.showQualityMenu = !wasOpen;
    if (this.showQualityMenu) {
      this.showVideoControls();
    }
  }

  getQualityDisplayLabel(quality: any): { main: string, sub: string } {
    if (quality.isAuto) {
      return { main: 'Auto', sub: '' };
    }
    if (quality.height >= 1080) {
      return { main: 'Full HD', sub: `Up to ${quality.height}p` };
    }
    if (quality.height >= 720) {
      return { main: 'HD', sub: `Up to ${quality.height}p` };
    }
    if (quality.height >= 480) {
      return { main: 'SD', sub: `Up to ${quality.height}p` };
    }
    return { main: 'Data Saver', sub: `${quality.height}p` };
  }

  skipForward(seconds: number = 10) {
    if (this.duration > 0) {
      this.videoElement.currentTime = Math.min(this.duration, this.videoElement.currentTime + seconds);
    }
  }

  skipBackward(seconds: number = 10) {
    this.videoElement.currentTime = Math.max(0, this.videoElement.currentTime - seconds);
  }

  displaySkipIndicator(text: string, side: 'left' | 'right') {
    const wasAlreadyShowing = this.showSkipIndicator;
    this.skipIndicatorText = text;
    this.skipIndicatorSide = side;
    this.showSkipIndicator = true;
    if (wasAlreadyShowing) {
      const el = this.videoContainer.nativeElement.querySelector('.skip-indicator');
      this.renderer.addClass(el, 'updated');
      setTimeout(() => {
        this.renderer.removeClass(el, 'updated');
      }, 200);
    }
    if (this.skipIndicatorTimer) {
      clearTimeout(this.skipIndicatorTimer);
    }
    this.skipIndicatorTimer = setTimeout(() => {
      this.showSkipIndicator = false;
      this.cdr.detectChanges();
    }, this.DOUBLE_TAP_SEQUENCE_WINDOW);
    this.cdr.detectChanges();
  }

  private skipIndicatorTimer: any = null;

  private resetSeekAccumulator() {
    if (this.doubleTapSequenceTimer) {
      clearTimeout(this.doubleTapSequenceTimer);
    }
    this.doubleTapSequenceTimer = setTimeout(() => {
      this.doubleTapSeekAmount = 0;
      this.doubleTapSequenceTimer = null;
    }, this.DOUBLE_TAP_SEQUENCE_WINDOW);
  }

  @HostListener('document:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent) {
    if (this.isAdPlaying) return;
    if (document.activeElement?.tagName.toLowerCase() === 'input' || 
        document.activeElement?.tagName.toLowerCase() === 'textarea') {
      return;
    }
    const handledKeys = ['Space', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'KeyM', 'KeyF', 'Escape'];
    if (handledKeys.includes(event.code)) {
      event.preventDefault();
      this.showVideoControls(); 
    }
    switch (event.code) {
      case 'Space':
        this.togglePlay();
        break;
      case 'ArrowLeft':
        this.skipBackward(5);
        this.doubleTapSeekAmount -= 5;
        this.displaySkipIndicator(`${this.doubleTapSeekAmount}s`, 'left');
        this.resetSeekAccumulator();
        break;
      case 'ArrowRight':
        this.skipForward(5);
        this.doubleTapSeekAmount += 5;
        this.displaySkipIndicator(`+${this.doubleTapSeekAmount}s`, 'right');
        this.resetSeekAccumulator();
        break;
      case 'ArrowUp':
        this.videoElement.volume = Math.min(1, this.videoElement.volume + 0.1);
        break;
      case 'ArrowDown':
        this.videoElement.volume = Math.max(0, this.videoElement.volume - 0.1);
        break;
      case 'KeyM':
        this.toggleMute();
        break;
      case 'KeyF':
        this.toggleFullscreen();
        break;
      case 'Escape':
        this.closeAllMenus();
        if (this.isFullscreen) {
          this.toggleFullscreen();
        }
        break;
    }
  }

  formatTime(seconds: number): string {
    if (isNaN(seconds) || seconds < 0) return '0:00';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  }

  getVolumeIcon(): string {
    if (this.isMuted || this.volume === 0) return 'volume_off';
    if (this.volume < 0.5) return 'volume_down';
    return 'volume_up';
  }

  onBackButtonClick() {
    if (this.isDestroyed) return;
    this.onBackClick.emit();
  }

  private debounce<T extends (...args: any[]) => void>(func: T, wait: number) {
    let timeout: any;
    return (...args: Parameters<T>) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func(...args), wait);
    };
  }

  getAutoQualityLabel(): string {
    try {
      const activeVariant = this.player?.getVariantTracks()?.find((t: any) => t.active);
      if (activeVariant && activeVariant.height) {
        return `Auto (${activeVariant.height}p)`;
      }
    } catch (e) {
      console.warn("auto-quality-label-error", e);
    }
    return "Auto";
  }  

  checkSegment() {
    if (!this.segments || !this.segments.length || this.isAdPlaying) return;
    const t = this.currentTime;
    this.currentSegment = this.segments.find(
      seg => t >= seg.startTime && t <= seg.endTime
    );
  }

  shouldShowSkipButton(): boolean {
    return (
      this.currentSegment &&
      this.currentSegment.skippable === true &&
      this.currentTime >= this.currentSegment.startTime &&
      this.currentTime <= this.currentSegment.endTime
    );
  }
  
  skipCurrentSegment() {
    if (!this.currentSegment) return;
    this.seek(this.currentSegment.endTime);
    this.showVideoControls();
  }  

  private enableAutoOrientation() {
    if (this.orientationAutoEnabled) return;
    this.orientationAutoEnabled = true;
    window.addEventListener("orientationchange", this.handleAutoRotation);
  }
  
  private handleAutoRotation = async () => {
    if (!this.orientationAutoEnabled || !this.isMobile()) return;
    const angle = screen.orientation?.angle || window.orientation;
    if (angle === 90 || angle === -90) {
      await this.enterAutoFullscreenLandscape();
    } else {
      await this.exitAutoFullscreenPortrait();
    }
  };

  private async enterAutoFullscreenLandscape() {
    const container = this.videoContainerRef.nativeElement;
    if (!this.isFullscreen) {
      try {
        await container.requestFullscreen();
      } catch {}
    }
    if (this.isOrientationAPISupported()) {
      try {
        await (screen.orientation as any).lock('landscape');
      } catch {}
    }
    this.isFullscreen = true;
    this.cdr.detectChanges();
  }  

  private async exitAutoFullscreenPortrait() {
    if (this.isFullscreen) {
      try {
        await document.exitFullscreen();
      } catch {}
    }
    if (this.isOrientationAPISupported()) {
      try {
        (screen.orientation as any).unlock();
      } catch {}
    }
    this.isFullscreen = false;
    this.cdr.detectChanges();
  }  
  
  async handleShakaError(error: any) {
    console.error("❌ SHAKA ERROR:", error);
    Sentry.captureException(new Error(`Shaka Player Error: ${error?.code} - ${error?.message || ''}`), {
      tags: {
        'video.id': this.video?.vidLibId || '',
        'shaka.error_code': error?.code,
        'shaka.category': error?.category,
        'shaka.severity': error?.severity,
      },
      extra: {
        media_url: this.lastMediaUrl,
        is_drm: !this.isHls(),
        is_hls: this.isHls(),
        video_title: this.title,
      },
    });
    // RECOVERABLE errors: Shaka can handle internally (e.g. stale persistent
    // DRM session restoration). Don't flash the error UI for these.
    if (error?.severity === shaka.util.Error.Severity.RECOVERABLE) {
      console.warn("⚠️ Recoverable Shaka error (no UI shown):", error?.code);
      return;
    }
    this.isLoading = false;
    this.isBuffering = false;
    this.hasPlaybackError = true;
    const code = error?.code;
    // Detect geo-restriction: Shaka BAD_HTTP_STATUS has HTTP status in error.data[1]
    // 403 = Forbidden (CDN geo-block), 451 = Unavailable for Legal Reasons
    const httpStatus = error?.data?.[1];
    if (this.isHls() && (code === shaka.util.Error.Code.BAD_HTTP_STATUS || code === shaka.util.Error.Code.BAD_NETWORK || code === shaka.util.Error.Code.MANIFEST_UNAVAILABLE) && (httpStatus === 403 || httpStatus === 451)) {
      this.isGeoRestricted = true;
      this.hasPlaybackError = false;
      this.cdr.detectChanges();
      return;
    }
    const isNetworkError =
      code === shaka.util.Error.Code.BAD_NETWORK ||
      code === shaka.util.Error.Code.BAD_HTTP_STATUS ||
      code === shaka.util.Error.Code.HTTP_ERROR ||
      code === shaka.util.Error.Code.TIMEOUT ||
      code === shaka.util.Error.Code.MANIFEST_UNAVAILABLE;
    if (this.isHls() && this.isAes128Hls) {
      this.errorMessage = "Unable to fetch stream key. Retrying...";
      this.cdr.detectChanges();
      if (isNetworkError && this.autoRetryCount < this.MAX_AUTO_RETRY) {
        const delay = this.RETRY_DELAYS[this.autoRetryCount] ?? 2500;
        this.autoRetryCount++;
        this.scheduleRetry(delay, `AES-128 HLS key/network failure (code=${code})`);
        return;
      }
      this.errorMessage = "Unable to fetch stream key. Please refresh.";
      this.cdr.detectChanges();
      return;
    }
    switch (code) {
      case shaka.util.Error.Code.DRM_LICENSE_REQUEST_FAILED:
      case shaka.util.Error.Code.DRM_SERVER_CERTIFICATE_REQUEST_FAILED:
        this.errorMessage = "Unable to obtain license. Playback restricted.";
        break;
      case shaka.util.Error.Code.LICENSE_RESPONSE_REJECTED:
      case shaka.util.Error.Code.DRM_SESSION_NOT_USABLE:
        this.errorMessage = "DRM License rejected. Content may require subscription.";
        break;
      case shaka.util.Error.Code.BAD_NETWORK:
      case shaka.util.Error.Code.BAD_HTTP_STATUS:
      case shaka.util.Error.Code.MANIFEST_UNAVAILABLE:
        this.errorMessage = "Network issue loading video. Check your connection.";
        break;
      case shaka.util.Error.Code.MANIFEST_PARSER_NOT_FOUND:
        this.errorMessage = "Invalid stream format.";
        break;
      default:
        this.errorMessage = "Playback error occurred.";
    }
    this.cdr.detectChanges();
    if (!this.isHls() && code === shaka.util.Error.Code.DRM_LICENSE_REQUEST_FAILED) {
      if (this.autoRetryCount < this.MAX_AUTO_RETRY) {
        const delay = this.RETRY_DELAYS[this.autoRetryCount] ?? 2500;
        this.autoRetryCount++;
        this.scheduleRetry(delay, `DRM license failure (code=${code})`);
      }
    }
    // Track DRM failures for billing dashboard
    const isDrmError =
      code === shaka.util.Error.Code.DRM_LICENSE_REQUEST_FAILED ||
      code === shaka.util.Error.Code.DRM_SERVER_CERTIFICATE_REQUEST_FAILED ||
      code === shaka.util.Error.Code.LICENSE_RESPONSE_REJECTED ||
      code === shaka.util.Error.Code.DRM_SESSION_NOT_USABLE;

    if (isDrmError) {
      const failedVideoId = this.video?.vidLibId?.toString() || '';
      this.trackDrmLicence(failedVideoId, 'failed', code, error?.message || this.errorMessage);
    }
    if (isNetworkError) {
      console.warn("⚠️ Network/segment error → retryStreaming");
      this.hasPlaybackError = false;
      this.errorMessage = "";
      this.isBuffering = true;
      this.isLoading = true;
      this.cdr.detectChanges();
      try {
        await this.player.retryStreaming();
        this.isBuffering = false;
        this.isLoading = false;
        return;
      } catch (e) {
        console.warn("retryStreaming failed", e);
      }
      const t = this.videoElement.currentTime || 0;
      this.seek(t + 6);
      console.warn('seeking 6s')
      return;
    }
    // if (isNetworkError) {
    //   console.warn("⚠️ Segment error, skipping 10s...");
    //   const t = this.videoElement.currentTime;
    //   this.seek(t + 10);
    //   return;
    // }
  }
  // handleShakaError(error: any) {
  //   console.error("❌ SHAKA ERROR:", error);
  //   this.isLoading = false;
  //   this.isBuffering = false;
  //   this.hasPlaybackError = true;
  //   switch (error.code) {
  //     case shaka.util.Error.Code.BAD_HTTP_STATUS:
  //     case shaka.util.Error.Code.DRM_LICENSE_REQUEST_FAILED:
  //     case shaka.util.Error.Code.DRM_SERVER_CERTIFICATE_REQUEST_FAILED:
  //       this.errorMessage = "Unable to obtain license. Playback restricted.";
  //       break;
  //     case shaka.util.Error.Code.LICENSE_RESPONSE_REJECTED:
  //     case shaka.util.Error.Code.DRM_SESSION_NOT_USABLE:
  //       this.errorMessage = "DRM License rejected. Content may require subscription.";
  //       break;
  //     case shaka.util.Error.Code.MANIFEST_UNAVAILABLE:
  //     case shaka.util.Error.Code.BAD_NETWORK:
  //       this.errorMessage = "Network issue loading video. Check your connection.";
  //       break;
  //     case shaka.util.Error.Code.MANIFEST_PARSER_NOT_FOUND:
  //       this.errorMessage = "Invalid stream format.";
  //       break;
  //     default:
  //       this.errorMessage = "Playback error occurred.";
  //     }
  //   this.cdr.detectChanges();
  //   if (error.code === shaka.util.Error.Code.DRM_LICENSE_REQUEST_FAILED && this.autoRetryCount < this.MAX_AUTO_RETRY) {
  //     this.autoRetryCount++;
  //     setTimeout(() => this.retryPlayback(), 800);
  //   }
  // }
  
  private trackDrmLicence(videoId: string, status = 'success', errorCode?: number, failureReason?: string) {
    const userId = localStorage.getItem('userId') || '';
    const channelId = this.video?.channelId || localStorage.getItem('wl_content_provider_id') || '';
    const payload = {
      videoId: videoId,
      channelId: channelId,
      userId: userId,
      drmLicenseAcquisitionCount: String(this.drmLicenceCount),
      platformType: this.deviceService.getPlatformType(),
      videoType: this.video?.preparedVideoType || this.video?.videoType || 'premium',
      status: status,
      failureReason: failureReason || null,
      errorCode: errorCode ? String(errorCode) : null,
      retryCount: String(this.autoRetryCount)
    };
    this.http.post(URLS.trackDrmLicence, payload).subscribe({
      error: (err) => console.warn('DRM licence tracking failed:', err)
    });
  }

  private scheduleRetry(delayMs: number, reason: string) {
    if (this.isDestroyed) return;
    if (this.retryTimer) clearTimeout(this.retryTimer);
  
    console.warn(`🔁 Auto-retry scheduled in ${delayMs}ms. Reason: ${reason}`);
  
    this.retryTimer = setTimeout(() => {
      if (this.isDestroyed) return;
      this.retryPlayback();
    }, delayMs);
  }
  
  async retryPlayback() {
    try {
      this.hasPlaybackError = false;
      this.errorMessage = "";
      this.isLoading = true;
      if (this.player) {
        await this.player.unload().catch(()=>{});
        await this.player.destroy().catch(()=>{});
        this.player = null;
      }
      await this.initShakaPlayer(this.lastMediaUrl || this.src, this.drmConfig);
    } catch (e) {
      console.error("retry failed", e);
    }
  }
  // retryPlayback() {
  //   this.hasPlaybackError = false;
  //   this.errorMessage = "";
  //   this.isLoading = true;
  //   this.initShakaPlayer(this.src, this.drmConfig); 
  // }

  private loadImaSdk(): Promise<void> {
    return new Promise((resolve, reject) => {
      if ((window as any).google?.ima) {
        resolve();
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://imasdk.googleapis.com/js/sdkloader/ima3.js';
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => reject('IMA failed');
      document.head.appendChild(script);
    });
  }

  private async initAds() {
    if (!this.shouldPlayAds()) return;
    await this.loadImaSdk();
    if (!shaka.ads || !this.player) return;
    this.shakaUi = new shaka.ui.Overlay(
      this.player,
      this.videoContainerRef.nativeElement,
      this.videoElement
    );
    this.shakaUi.configure({
      addBigPlayButton: false,
      addSeekBar: false,
      controlPanelElements: [],
      overflowMenuButtons: [],
      customContextMenu: false,
      enableKeyboardPlaybackControls: false,
    });
    this.shakaControls = this.shakaUi.getControls();
    // this.disableShakaControls();
    this.adManager = this.player.getAdManager();
    this.adContainer = this.shakaControls.getClientSideAdContainer();
    const settings = new google.ima.AdsRenderingSettings();
    settings.restoreCustomPlaybackStateOnAdBreakComplete = true;
    this.adManager.initClientSide(
      this.adContainer,
      this.videoElement,
      settings
    );
    this.registerAdEvents();
    this.requestAds();
  }
  
  private shouldPlayAds(): boolean {
    if (!this.video) return false;
    if (this.isHls()) return false;
    if (!this.video?.vmapUrl) return false;
    if (this.contentType === 'trailer') return false;
    if (this.playbackLimitExceeded) return false;
    return true;
  }
  
  private requestAds() {
    const adsRequest = new google.ima.AdsRequest();
    adsRequest.adTagUrl = this.video?.vmapUrl;
    adsRequest.linearAdSlotWidth = this.videoElement.clientWidth;
    adsRequest.linearAdSlotHeight = this.videoElement.clientHeight;
    adsRequest.nonLinearAdSlotWidth = this.videoElement.clientWidth;
    adsRequest.nonLinearAdSlotHeight = this.videoElement.clientHeight / 3;
    this.adManager.requestClientSideAds(adsRequest);
  }
  
  private registerAdEvents() {
    // A new ad in the same break is loading/starting -> cancel any pending
    // teardown so the UI stays in "ad mode" across a pod (Ad 1 of 2 -> 2 of 2).
    const cancelPendingAdEnd = () => {
      if (this.adEndTimer) {
        clearTimeout(this.adEndTimer);
        this.adEndTimer = null;
      }
    };
    this.adManager.addEventListener('ad-loaded', cancelPendingAdEnd);

    this.adManager.addEventListener('ad-started', () => {
      cancelPendingAdEnd();
      this.isAdPlaying = true;
      this.stopWatchTimer();
      const container = this.videoContainerRef.nativeElement;
      this.renderer.addClass(container,'ad-playing');
      this.renderer.removeClass(container,'cursor-hidden');
      clearTimeout(this.mouseIdleTimer);
      clearTimeout(this.controlsTimeout);
      clearTimeout(this.hideTimeout);
      this.disableCustomControls();
      this.enableShakaControls();

      // Ad transparency: detect ad type from VAST Description metadata
      try {
        const ad = this.adManager.getCurrentAd();
        if (ad) {
          this.adRemainingTime = Math.ceil(ad.getDuration ? ad.getDuration() : 0);
          const adDesc = ad.getDescription ? ad.getDescription() : '';
          this.adBadgeType = (adDesc && adDesc.includes('adType:promo')) ? 'promo' : 'ad';
          const clickMatch = adDesc ? adDesc.match(/clickUrl:(.+?)(?:\||$)/) : null;
          this.adClickUrl = clickMatch ? clickMatch[1] : null;
        }
      } catch (e) {
        console.warn('Ad metadata parse error:', e);
        this.adBadgeType = 'ad';
        this.adClickUrl = null;
      }
      this.startAdCountdown();

      this.cdr.detectChanges();
    });
    const onAdEnd = () => {
      this.adEndTimer = null;
      this.isAdPlaying = false;
      this.stopAdCountdown();
      this.adClickUrl = null;
      this.adBadgeType = 'ad';
      this.renderer.removeClass(this.videoContainerRef.nativeElement,'ad-playing');
      this.resetMouseIdleTimer();
      if (this.pendingSeekTime != null) {
        this.seek(this.pendingSeekTime);
        this.pendingSeekTime = null;
      }
      this.disableShakaControls();
      this.enableCustomControls();
      if (!this.playbackLimitExceeded) {
        this.videoElement.play().catch(() => {});
      }
      this.cdr.detectChanges();
    };

    // Per-ad terminal events end a *break* (pod), not the whole VMAP. We can't
    // listen for a "content-resume" event (Shaka 4.5 doesn't expose one), so we
    // debounce: schedule teardown after each ad ends, but the next ad in the
    // same pod cancels it via ad-loaded/ad-started above. The teardown therefore
    // runs only once content actually resumes, restoring controls between breaks
    // instead of staying frozen until the final post-roll.
    const scheduleAdEnd = () => {
      if (this.adEndTimer) clearTimeout(this.adEndTimer);
      this.adEndTimer = setTimeout(onAdEnd, 400);
    };
    this.adManager.addEventListener('ad-stopped', scheduleAdEnd);
    this.adManager.addEventListener('ad-skipped', scheduleAdEnd);
    this.adManager.addEventListener('ad-error', scheduleAdEnd);
    // Whole-VMAP end: tear down immediately (clears any pending debounce).
    this.adManager.addEventListener('all-ads-completed', onAdEnd);
  }
  
  private startAdCountdown(): void {
    this.stopAdCountdown();
    this.adCountdownInterval = setInterval(() => {
      if (this.adManager) {
        try {
          this.adRemainingTime = Math.ceil(this.adManager.getRemainingTime());
        } catch (e) {
          // getRemainingTime may throw if no ad is active
        }
        if (this.adRemainingTime <= 0) {
          this.stopAdCountdown();
        }
        this.cdr.detectChanges();
      }
    }, 1000);
  }

  private stopAdCountdown(): void {
    if (this.adCountdownInterval) {
      clearInterval(this.adCountdownInterval);
      this.adCountdownInterval = null;
    }
    this.adRemainingTime = 0;
  }

  formatAdTime(seconds: number): string {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return m + ':' + (s < 10 ? '0' : '') + s;
  }

  private enableShakaControls() {
    if (!this.shakaControls) return;
    this.shakaControls.setEnabled(true);
    this.shakaControls.show();
  }
  
  private disableShakaControls() {
    if (!this.shakaControls) return;
    // this.shakaControls.setEnabled(false);
    // this.shakaControls.hide();
  }
  
  private disableCustomControls() {
    this.isControlsVisible = false;
  }
  
  private enableCustomControls() {
    this.isControlsVisible = true;
  }  

  private isHls(): boolean {
    return this.src?.includes('.m3u8');
  }

  private async waitForToken(maxWaitMs = 5000): Promise<string> {
    const start = Date.now();
    while (Date.now() - start < maxWaitMs) {
      const t = this.axinomService.getWidevineToken();
      if (t) return t;
      await new Promise(r => setTimeout(r, 100));
    }
    throw new Error("DRM token not ready");
  }

  private async detectAes128Hls(mediaUrl: string): Promise<boolean> {
    try {
      if (!mediaUrl?.includes('.m3u8')) return false;
      const res = await fetch(mediaUrl, { cache: 'no-store' });
      if (!res.ok) return false;
      const text = await res.text();
      return /#EXT-X-KEY:METHOD=AES-128/i.test(text);
    } catch {
      return false;
    }
  }

  private preferStartQuality(targetHeight = 480) {
    try {
      const variants = this.player.getVariantTracks();
      if (!variants?.length) return;
      const sorted = variants.filter((v: any) => v.height).sort((a: any, b: any) => a.height - b.height);
      let chosen = sorted.filter((v: any) => v.height <= targetHeight).pop();
      if (!chosen) chosen = sorted[0]; 
      this.player.configure({ abr: { enabled: false } });
      this.player.selectVariantTrack(chosen, true);
      console.log(`✅ Start quality forced: ${chosen.height}p`);
    } catch (e) {
      console.warn("preferStartQuality failed", e);
    }
  }
}